import React, { useRef } from 'react';
import styled from 'styled-components';
import { LineHeightProps } from 'styled-system';

import { FilestackImageData } from 'api/types/shared.types';
import { Box } from 'components/Box';
import { Hero } from 'components/Hero';
import { SearchMenu } from 'components/Search/SearchMenu';
import { H1, RichText } from 'components/Text';
import HideContainer from 'containers/HideContainer';
import {
  breakpoints,
  colors,
  fontSizesByType,
  gutterWidth,
  maxWidth,
  shadows,
  transition,
} from 'theme';
import { useSticky, useViewportBreakpointValue } from 'utils/screen';

const Title = styled.div<LineHeightProps>`
  font-size: ${fontSizesByType.h3[0]}px;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h3[2]}px;
  }
`;

const FixedMenu = styled.div<{ $sticky?: boolean }>`
  ${({ $sticky }) =>
    `transform: ${
      $sticky ? 'translateY(0)' : 'translateY(-100%) translateY(-4px)'
    };`}

  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  box-shadow: ${shadows.toolbar};
  transition: all ${transition};
  will-change: transform;
`;

type Props = {
  title?: string;
  image: FilestackImageData;
  imageMobile: FilestackImageData | undefined;
  onSearch: (params: {
    query?: string;
    type?: string;
    searchType?: string;
  }) => void;
  searchMode?: boolean;
  navSection: 'MAIN' | 'STAFF';
  defaultSearchType?: ListingType;
  showListingCounts?: boolean;
};

export function SearchHero({
  title,
  image,
  imageMobile,
  onSearch,
  searchMode,
  navSection,
  defaultSearchType,
  showListingCounts,
}: Props) {
  const offsetTop = useViewportBreakpointValue({
    xs: 64,
    lg: 96 + (showListingCounts ? 80 : 0),
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const menuIsSticky = useSticky({
    ref: menuRef,
    offset: { top: offsetTop },
    onlyIfScrolledPast: true,
  });

  return (
    <>
      <Hero
        pb={[36, null, null, 64]}
        pt={[36, null, null, 80]}
        minHeight={['470px', null, null, 'calc(100vh - 300px)']}
        heroImage={image}
        mobileHeroImage={imageMobile}
        data-qa-id="search-hero"
        alwaysShow
      >
        {!searchMode && (
          <Box
            width="100%"
            maxWidth={[400, null, null, maxWidth[3]]}
            px={[0, null, null, gutterWidth]}
            m="0 auto"
          >
            <Box
              width={[1, null, null, 1 / 2]}
              px={[20, null, null, 60]}
              py={[20, null, null, 40]}
              borderRadius={12}
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
              bg="white"
              color={colors.greyishBrown}
            >
              <RichText mb={20}>
                <H1>
                  <Title
                    lineHeight={1.3}
                    dangerouslySetInnerHTML={{
                      __html:
                        title?.replace(/<(\/?)(p|h1)>/g, '<$1span>') || '',
                    }}
                  />
                </H1>
              </RichText>

              <SearchMenu
                searchMode={searchMode}
                navSection={navSection}
                onSearch={onSearch}
                defaultSearchType={defaultSearchType}
                qaId="search-hero-menu"
                searchFrom="Search Hero"
              />
            </Box>
          </Box>
        )}
      </Hero>

      <div ref={menuRef}>
        <HideContainer xs sm md>
          <FixedMenu $sticky={menuIsSticky}>
            <SearchMenu
              searchMode={searchMode}
              navSection={navSection}
              onSearch={onSearch}
              defaultSearchType={defaultSearchType}
              inPageHeader
              qaId="search-hero-menu-fixed"
              searchFrom="Header Search Bar"
            />
          </FixedMenu>
        </HideContainer>
      </div>
    </>
  );
}
